import { useEffect, useRef, useState, type FC, type PointerEvent, type ReactNode } from "react";
import styles from "./ZoomableSectionPreview.module.css";

const SECTION_PREVIEW_DEFAULT_ZOOM = 1;
const SECTION_PREVIEW_MIN_ZOOM = SECTION_PREVIEW_DEFAULT_ZOOM;
const SECTION_PREVIEW_MAX_ZOOM = 4;
const SECTION_PREVIEW_ZOOM_FACTOR = 1.16;
const SECTION_PREVIEW_PAN_START_DISTANCE_PX = 3;

type SectionPreviewZoom = {
  scale: number;
  translateX: number;
  translateY: number;
};

export type SectionPreviewSize = {
  height: number;
  width: number;
};

type SectionPreviewPanState = {
  didPan: boolean;
  latestZoom: SectionPreviewZoom;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startZoom: SectionPreviewZoom;
};

type ZoomableSectionPreviewProps = {
  ariaLabel: string;
  children: ReactNode;
  onPreviewSizeChange: (previewSize: SectionPreviewSize) => void;
  previewSize: SectionPreviewSize;
  resetKey: string;
};

const DEFAULT_SECTION_PREVIEW_ZOOM: SectionPreviewZoom = {
  scale: SECTION_PREVIEW_DEFAULT_ZOOM,
  translateX: 0,
  translateY: 0,
};

export const getPreviewSize = (width = 760): SectionPreviewSize => {
  const safeWidth = width > 0 ? width : 760;

  return {
    width: safeWidth,
    height: Math.max(safeWidth / (760 / 400), 288),
  };
};

const clampNumber = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const clampSectionPreviewZoom = (zoom: SectionPreviewZoom, viewportSize: SectionPreviewSize): SectionPreviewZoom => {
  const scale = clampNumber(zoom.scale, SECTION_PREVIEW_MIN_ZOOM, SECTION_PREVIEW_MAX_ZOOM);

  if (scale === SECTION_PREVIEW_DEFAULT_ZOOM) {
    return DEFAULT_SECTION_PREVIEW_ZOOM;
  }

  const minTranslateX = Math.min(0, viewportSize.width - viewportSize.width * scale);
  const minTranslateY = Math.min(0, viewportSize.height - viewportSize.height * scale);

  return {
    scale,
    translateX: clampNumber(zoom.translateX, minTranslateX, 0),
    translateY: clampNumber(zoom.translateY, minTranslateY, 0),
  };
};

const ZoomableSectionPreview: FC<ZoomableSectionPreviewProps> = ({
  ariaLabel,
  children,
  onPreviewSizeChange,
  previewSize,
  resetKey,
}) => {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const panStateRef = useRef<SectionPreviewPanState | null>(null);
  const [previewZoom, setPreviewZoom] = useState<SectionPreviewZoom>(DEFAULT_SECTION_PREVIEW_ZOOM);
  const [isPanning, setIsPanning] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const updatePreviewSize = (width: number) => onPreviewSizeChange(getPreviewSize(width));

    updatePreviewSize(canvas.getBoundingClientRect().width);

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(([entry]) => {
        if (!entry) {
          return;
        }

        updatePreviewSize(entry.contentRect.width);
      });

      resizeObserver.observe(canvas);

      return () => resizeObserver.disconnect();
    }

    const handleWindowResize = () => updatePreviewSize(canvas.getBoundingClientRect().width);

    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
  }, [onPreviewSizeChange]);

  useEffect(() => {
    panStateRef.current = null;
    setIsPanning(false);
    setPreviewZoom(DEFAULT_SECTION_PREVIEW_ZOOM);
  }, [resetKey]);

  useEffect(() => {
    setPreviewZoom((currentZoom) => clampSectionPreviewZoom(currentZoom, previewSize));
  }, [previewSize]);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const handlePreviewWheel = (event: globalThis.WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.deltaY === 0) {
        return;
      }

      const rect = canvas.getBoundingClientRect();

      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }

      const cursorX = clampNumber(event.clientX - rect.left, 0, rect.width);
      const cursorY = clampNumber(event.clientY - rect.top, 0, rect.height);
      const wheelZoomFactor = event.deltaY < 0 ? SECTION_PREVIEW_ZOOM_FACTOR : 1 / SECTION_PREVIEW_ZOOM_FACTOR;

      setPreviewZoom((currentZoom) => {
        const nextScale = clampNumber(
          currentZoom.scale * wheelZoomFactor,
          SECTION_PREVIEW_MIN_ZOOM,
          SECTION_PREVIEW_MAX_ZOOM,
        );

        if (nextScale === currentZoom.scale) {
          return currentZoom;
        }

        const imagePointX = (cursorX - currentZoom.translateX) / currentZoom.scale;
        const imagePointY = (cursorY - currentZoom.translateY) / currentZoom.scale;

        return clampSectionPreviewZoom(
          {
            scale: nextScale,
            translateX: cursorX - imagePointX * nextScale,
            translateY: cursorY - imagePointY * nextScale,
          },
          { width: rect.width, height: rect.height },
        );
      });
    };

    canvas.addEventListener("wheel", handlePreviewWheel, { passive: false });

    return () => canvas.removeEventListener("wheel", handlePreviewWheel);
  }, []);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || previewZoom.scale <= SECTION_PREVIEW_DEFAULT_ZOOM) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    panStateRef.current = {
      didPan: false,
      latestZoom: previewZoom,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startZoom: previewZoom,
    };
    setIsPanning(false);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const panState = panStateRef.current;

    if (!panState || panState.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const deltaX = event.clientX - panState.startClientX;
    const deltaY = event.clientY - panState.startClientY;
    const hasStartedPanning =
      panState.didPan || Math.hypot(deltaX, deltaY) > SECTION_PREVIEW_PAN_START_DISTANCE_PX;

    if (!hasStartedPanning) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const nextZoom = clampSectionPreviewZoom(
      {
        scale: panState.startZoom.scale,
        translateX: panState.startZoom.translateX + deltaX,
        translateY: panState.startZoom.translateY + deltaY,
      },
      { width: rect.width, height: rect.height },
    );

    panState.didPan = true;
    panState.latestZoom = nextZoom;
    setIsPanning(true);
    setPreviewZoom(nextZoom);
  };

  const finishPanning = (event: PointerEvent<HTMLDivElement>) => {
    const panState = panStateRef.current;

    if (!panState || panState.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    panStateRef.current = null;
    setIsPanning(false);
    setPreviewZoom(panState.latestZoom);
  };

  const isPreviewZoomed = previewZoom.scale > SECTION_PREVIEW_DEFAULT_ZOOM;
  const canvasClassName = [
    styles.canvas,
    isPreviewZoomed ? styles.canvasZoomed : "",
    isPanning ? styles.canvasPanning : "",
  ]
    .filter(Boolean)
    .join(" ");
  const drawingTransform = `matrix(${previewZoom.scale} 0 0 ${previewZoom.scale} ${previewZoom.translateX} ${previewZoom.translateY})`;

  return (
    <div
      ref={canvasRef}
      className={canvasClassName}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishPanning}
      onPointerCancel={finishPanning}
    >
      <svg
        className={styles.drawing}
        viewBox={`0 0 ${previewSize.width} ${previewSize.height}`}
        role="img"
        aria-label={ariaLabel}
      >
        <g transform={drawingTransform}>{children}</g>
      </svg>
    </div>
  );
};

export default ZoomableSectionPreview;
